import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withRouter } from 'react-router-dom';

import { Fields } from 'redux-form';

import { withStyles } from '@material-ui/core/styles';
import withWidth from '@material-ui/core/withWidth';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import { intersection } from 'lodash';

import Menu, { MenuItem } from 'components/common/Menu';
import Link from 'components/common/Link';
import IconView from 'components/icons/IconView';
import Tooltip from 'components/common/Tooltip';
import IconButton from 'components/common/IconButton';

import Badge from 'components/common/Badge';
import IconArticle from 'components/icons/IconArticle';
import IconCrossBold from 'components/icons/IconCrossBold';
import NarrativeViewDialog from 'components/workflow/NarrativeViewDialog';
import ClaimLogViewDialog from 'components/workflow/ClaimLogViewDialog';
import DocumentsViewDialog from 'components/common/DocumentsViewDialog';
import WorkflowState from 'components/common/WorkflowState';

import formatPrice from 'utils/formatPrice';
import sortInsurance from 'utils/sortInsurance';
import formatUtcToLocalDate from 'utils/formatUtcToLocalDate';

import timeDateFormat from 'config/timeDateFormat';
import dateFormat from 'config/dateFormat';

import OrdersTableRowItemSelection from './OrdersTableRowItemSelection';
import OrdersTableRowItemStatusFields from './OrdersTableRowItemStatusFields';
import OrdersTableRowItemAssigned from './OrdersTableRowItemAssigned';
import OrdersTableRowItemDriver from './OrdersTableRowItemDriver';
import OrdersTableRowItemDelivery from './OrdersTableRowItemDelivery';
import OrdersTableRowItemReimbs from './OrdersTableRowItemReimbs';
import OrdersTableRowItemReimbsDate from './OrdersTableRowItemReimbsDate';
import OrdersTableRowItemBackorder from './OrdersTableRowItemBackorder';
import OrdersTableRowItemEditAddress from './OrdersTableRowItemEditAddress';
import OrdersTableRowItemCheckNumber from './OrdersTableRowItemCheckNumber';

const styles = theme => ({
  row: {
    height: 20,
  },
  rowHead: {
    borderTop: `1px solid ${theme.palette.primary.A100}`,
    '&:first-child': {
      borderTop: 'none',
    },
    '& $cell': {
      paddingTop: 12,
      fontSize: 12,
      letterSpacing: '0.03em',
      fontWeight: 700,
      whiteSpace: 'nowrap',
      color: theme.palette.grey[600],
    },
    '& $cellHidden': {
      fontSize: 0,
    },
  },
  rowBody: {
    '& $cell': {
      verticalAlign: 'top',
      paddingTop: 4,
      paddingBottom: 12,
    },
  },
  cell: {
    padding: '0 8px 0 8px',
    border: 'none',
    '&:last-child': {
      paddingRight: 16,
    },
  },
  cellFormLg: {
    width: '15.8%',
  },
  cellFormMd: {
    width: '12.9%',
  },
  cellDelivery: {
    width: 26,
  },
  cellDateOfDelivery: {
    width: 40,
  },
  cellFormSm: {
    width: '9.61%',
  },
  cellDateOfService: {
    width: 90,
  },
  cellBadgeInsurance: {
    width: 17,
  },
  cellReimbs: {
    width: 175,
  },
  cellReimbsDate: {
    width: 107,
  },
  cellCheckNumber: {
    width: 133,
  },
  cellHidden: {
    fontSize: 0,
  },
  cellActions: {
    position: 'relative',
    width: 30,
    paddingRight: 0,
    '&:first-child': {
      paddingLeft: 15,
    },
  },
  cellActionsConnectedOrders: {
    width: 5,
    paddingLeft: 5,
    '&:first-child': {
      paddingLeft: 5,
    },
  },
  selection: {
    marginTop: -15,
    marginRight: -10,
  },
  iconView: {
    width: 16,
    height: 12,
    padding: '13px 24px 13px 24px',
    margin: '-16px -24px -13px -16px',
    verticalAlign: 'middle',
  },
  link: {
    overflow: 'hidden',
  },
  linkTrimmedText: {
    display: 'inline-block',
    maxWidth: 80,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    verticalAlign: 'bottom',
    '&:hover, &:focus': {
      color: theme.palette.primary[600],
      opacity: 1,
      textShadow: '.4px 0 0 currentColor, -.4px 0 0  currentColor',
    },
    [theme.breakpoints.up('lg')]: {
      maxWidth: 200,
    },
    'span&': {
      '&:hover, &:focus': {
        color: theme.palette.text.primary,
        opacity: 1,
        textShadow: 'none',
      },
    },
  },
  linkTrimmedTextVariantCombinedConnected: {
    [theme.breakpoints.up('lg')]: {
      maxWidth: 80,
    },
    [theme.breakpoints.up('xl')]: {
      maxWidth: 200,
    },
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    position: 'absolute',
    height: 48,
    top: -62,
    left: 24,
    width: 600,
  },
  state: {
    marginRight: 16,
  },
  typographyTransmission: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    borderRadius: '50%',
    color: theme.palette.common.white,
    cursor: 'pointer',
    backgroundColor: theme.palette.grey[600],
  },
  typographyTransmissionApproved: {
    backgroundColor: theme.palette.add['500'],
  },
  typographyTransmissionCreated: {
    backgroundColor: theme.palette.validation['300'],
  },
  typographyTransmissionSend: {
    backgroundColor: theme.palette.notification['500'],
  },
  typographyTransmissionRejected: {
    backgroundColor: theme.palette.validation['500'],
  },
  claimLogViewButton: {
    margin: -12,
  },
  tooltipAssignedBy: {
    maxWidth: 70,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    marginRight: 3,
    color: theme.palette.text.primary,
    fontWeight: 400,
    textDecoration: 'underline',
    cursor: 'pointer',
    verticalAlign: 'bottom',
    '&:hover, &:focus': {
      color: theme.palette.primary[600],
      textShadow: '.4px 0 0 currentColor, -.4px 0 0  currentColor',
    },
    [theme.breakpoints.up('lg')]: {
      maxWidth: 100,
    },
    [theme.breakpoints.up('xl')]: {
      maxWidth: 150,
    },
  },
  reimbs: {
    '& + $reimbs': {
      marginTop: 8,
    },
  },
  reimbsDate: {
    '& + $reimbsDate': {
      marginTop: 8,
    },
  },
  checkNumber: {
    '& + $checkNumber': {
      marginTop: 8,
    },
  },
  badgeInsurance: {
    display: 'block',
    marginTop: 6,
    marginBottom: 6,
    '& + $badgeInsurance': {
      marginTop: 20,
    },
  },
  chip: {
    marginLeft: 8,
    marginRight: 8,
    backgroundColor: theme.palette.grey[600],
    borderColor: theme.palette.grey[600],
  },
  iconRejected: {
    position: 'relative',
  },
  iconArticle: {
    display: 'block',
    width: 22,
    height: 22,
    color: theme.palette.grey[600],
  },
  iconCrossBold: {
    position: 'absolute',
    right: 0,
    bottom: 0,
    width: 12,
    height: 12,
    color: theme.palette.validation[500],
  },
  tooltipTransmission: {
    position: 'relative',
    top: -7,
  },
  state: {
    top: -22,
    bottom: 4,
    height: 'auto',
  },
  tooltipRejected: {
    position: 'absolute',
    top: -23,
    left: 14,
  },
});

class OrdersTableRowItem extends Component {
  constructor(props) {
    super(props);

    this.state = {
      anchorEl: null,
      narrativeViewDialogOpen: false,
      claimLogViewDialogOpen: false,
      cmnViewDialogOpen: false,
    };

    this.onMenuButtonClick = this.onMenuButtonClick.bind(this);
    this.onMenuClose = this.onMenuClose.bind(this);
    this.onNarrativeMenuItemClick = this.onNarrativeMenuItemClick.bind(this);
    this.onNarrativeViewDialogClose = this.onNarrativeViewDialogClose.bind(this);
    this.onClaimLogViewButtonClick = this.onClaimLogViewButtonClick.bind(this);
    this.onClaimLogViewDialogClose = this.onClaimLogViewDialogClose.bind(this);
    this.onCMNMenuItemClick = this.onCMNMenuItemClick.bind(this);
    this.onCMNViewDialogClose = this.onCMNViewDialogClose.bind(this);
  }

  onMenuButtonClick(event) {
    this.setState({ anchorEl: event.currentTarget.querySelector('svg') });
  }

  onMenuClose() {
    this.setState({ anchorEl: null });
  }

  onNarrativeMenuItemClick() {
    this.setState({
      anchorEl: null,
      narrativeViewDialogOpen: true,
    });
  }

  onNarrativeViewDialogClose() {
    this.setState({ narrativeViewDialogOpen: false });
  }

  onClaimLogViewButtonClick() {
    this.setState({ claimLogViewDialogOpen: true });
  }

  onClaimLogViewDialogClose() {
    this.setState({ claimLogViewDialogOpen: false });
  }

  onCMNMenuItemClick() {
    this.setState({
      anchorEl: null,
      cmnViewDialogOpen: true,
    });
  }

  onCMNViewDialogClose() {
    this.setState({ cmnViewDialogOpen: false });
  }

  render() {
    const {
      expanded,
      ID,
      Type,
      Description,
      Narrative,
      Product,
      Charge,
      Units,
      Reimbs,
      Driver,
      Modifiers,
      variant,
      submitting,
      change,
      onTextFieldBlur,
      editAddressHref,
      onStockLineCreate,
      onHCPCCodeClick,
      containsNarrativeCMNAMB,
      width,
      Insurance,
      business,
      Patient,
      archive,
      classes,
      ...otherProps
    } = this.props;
    const {
      anchorEl,
      narrativeViewDialogOpen,
      claimLogViewDialogOpen,
      cmnViewDialogOpen,
    } = this.state;
    const open = Boolean(anchorEl);
    let backorderCount = 0;
    let transmissionStatus;
    let transmissionStatusType;
    if (otherProps['Claim Md Claim']
      && otherProps['Claim Md Claim'][0]
      && otherProps['Claim Md Claim'][0].Status) {
      const claimLinkAt = formatUtcToLocalDate(
        otherProps['Claim Md Claim'][0].Status[0]['Link At'],
        timeDateFormat,
        dateFormat,
        business['Company Address']['Time Zone'],
        business['Company Address']['Observes Daylight Saving'],
      );
      transmissionStatusType = otherProps['Claim Md Claim'][0].Status[0].Category;
      transmissionStatus = `${otherProps['Claim Md Claim'][0].Status[0].Status} ${claimLinkAt}`;
    }
    if (Product['Main Hcpc Code']
      && otherProps['HCPC S Code'].ID === Product['Main Hcpc Code'].ID) {
      backorderCount = -Number(Product['Stock Line Balance']);
    }
    const namePrefix = `${ID}-orderLine`;
    const State = otherProps['Workflow Status'].reduce((accumulator, currentValue) => {
      if (accumulator.filter(item => item.ID === currentValue['Workflow State'].ID).length === 0) {
        return [
          ...accumulator,
          currentValue['Workflow State'],
        ];
      }
      return accumulator;
    }, []);
    const StateID = State.map(item => item.ID);

    return (
      <Fragment>
        <TableRow
          className={classNames(classes.row, classes.rowHead)}
        >
          <TableCell
            className={classNames(classes.cell, classes.cellHidden, {
              [classes.cellActionsConnectedOrders]: variant === 'connected',
            })}
            padding="checkbox"
          >
            Actions
          </TableCell>
          <TableCell className={classes.cell}>Line Status</TableCell>
          <TableCell
            className={classes.cell}
            {...StateID.indexOf(2) !== -1 ? { colSpan: 1 } : { colSpan: 2 }}
          >
            {otherProps['Assigned From User'] &&
              <Tooltip
                className={classes.tooltipAssignedBy}
                disabled={!expanded}
                content={
                  <Typography>
                    {`${otherProps['Assigned From User']['First Name']} ${otherProps['Assigned From User']['Last Name']}`}
                  </Typography>
                }
                placement="top"
              >
                {otherProps['Assigned From User']['First Name']}
              </Tooltip>
            }
            Assigned to
          </TableCell>
          {StateID.indexOf(2) !== -1 &&
            <TableCell
              className={classes.cell}
            >
              Driver
            </TableCell>
          }
          <TableCell className={classNames(classes.cell, classes.cellDelivery)} />
          <TableCell className={classes.cell}>Type</TableCell>
          <TableCell
            className={classNames(classes.cell, classes.cellDateOfService)}
          >
            Date of Service
          </TableCell>
          <TableCell className={classes.cell}>HCPCS</TableCell>
          {intersection([1, 2, 3, 4, 5], StateID).length > 0 &&
            <TableCell
              className={classes.cell}
              {...(['combined', 'connected', 'workflow'].indexOf(variant) !== -1 && intersection([1, 3], StateID).length > 0)
                ? { colSpan: 2 } : {}}
            >
              Modifiers
            </TableCell>
          }
          {intersection([1, 2, 3, 4, 5], StateID).length > 0 &&
            <Fragment>
              <TableCell className={classes.cell}>Units</TableCell>
              {Product['Calories Per Unit'] &&
                <TableCell className={classes.cell}>Cal Units</TableCell>
              }
            </Fragment>
          }
          {intersection([1, 2, 3, 4, 5], StateID).length > 0 &&
            <TableCell
              className={classes.cell}
            >
              Charge
            </TableCell>
          }
          {intersection([4, 5], StateID).length > 0 &&
            <Fragment>
              <TableCell className={classNames(classes.cell, classes.cellBadgeInsurance)} />
              <TableCell className={classNames(classes.cell, classes.cellReimbs)}>
                Reimbs
              </TableCell>
              <TableCell
                className={classNames(classes.cell, classes.cellReimbsDate)}
                {...['combined', 'connected', 'workflow'].indexOf(variant) !== -1
                  ? { colSpan: 2 } : {}}
              >
                Reimbs Date
              </TableCell>
              <TableCell className={classes.cell}>
                Check Number
              </TableCell>
            </Fragment>
          }
          <TableCell
            className={classNames(classes.cell, classes.cellHidden)}
            {...(['connected'].indexOf(variant) !== -1 && intersection([1, 3], StateID).length > 0)
              ? { colSpan: 3 } : {}}
            {...(['combined', 'workflow'].indexOf(variant) !== -1 && intersection([1, 3], StateID).length > 0)
              ? { colSpan: 4 } : {}}
          >
            Transm
          </TableCell>
          {containsNarrativeCMNAMB &&
            <TableCell
              numeric
              className={classes.cell}
              {...(['combined', 'workflow'].indexOf(variant) !== -1 && intersection([1, 2, 3], StateID).length > 0)
                ? { colSpan: 2 } : {}}
            >
              {(Narrative || (otherProps.CMN && otherProps.CMN.ID)) &&
                'Narrative/CMN'
              }
            </TableCell>
          }
        </TableRow>
        <TableRow className={classNames(classes.row, classes.rowBody)}>
          <TableCell
            className={classNames(classes.cell, classes.cellActions, {
              [classes.cellActionsConnectedOrders]: variant === 'connected',
            })}
            padding="checkbox"
          >
            {variant !== 'connected' &&
              <OrdersTableRowItemSelection
                className={classes.selection}
                namePrefix={namePrefix}
                disabled={archive}
              />
            }
            {['combined', 'connected', 'workflow'].indexOf(variant) !== -1 &&
              <WorkflowState
                className={classes.state}
                workflowState={StateID}
                tooltipContent={State.map(item => item.Name).join(', ')}
              />
            }
            {backorderCount > 0 &&
              <OrdersTableRowItemBackorder
                count={backorderCount}
                ID={ID}
                Product={Product}
                onStockLineCreate={onStockLineCreate}
              />
            }
            {otherProps['Is Rejected'] &&
              <Tooltip
                className={classes.tooltipRejected}
                disabled={!expanded}
                content={
                  <Typography>
                    Rejected
                  </Typography>
                }
                placement="top"
              >
                <div className={classes.iconRejected}>
                  <IconArticle className={classes.iconArticle} />
                  <IconCrossBold className={classes.iconCrossBold} />
                </div>
              </Tooltip>
            }
            {/*{(['combined', 'connected', 'workflow'].indexOf(variant) !== -1 || backorderCount > 0 || editAddressHref) &&
              <div className={classes.actions}>          
                {editAddressHref &&
                  <OrdersTableRowItemEditAddress href={editAddressHref} />
                }
                
              </div>
            }*/}
          </TableCell>
          <TableCell
            className={classNames(classes.cell, {
              [classes.cellFormMd]: StateID.indexOf(1) === -1 || ['combined', 'connected', 'workflow'].indexOf(variant) !== -1,
              [classes.cellFormLg]: StateID.indexOf(1) !== -1 && ['combined', 'connected', 'workflow'].indexOf(variant) === -1,
            })}

          >
            <Fields
              names={[
                `${namePrefix}.Status Name`,
                `${namePrefix}.Status`,
              ]}
              namePrefix={namePrefix}
              disabled={submitting || archive}
              component={OrdersTableRowItemStatusFields}
            />
          </TableCell>
          <TableCell
            className={classNames(classes.cell, classes.cellFormMd, {
              [classes.cellFormSm]: StateID.indexOf(2) !== -1,
            })}
            {...StateID.indexOf(2) !== -1 ? { colSpan: 1 } : { colSpan: 2 }}
          >
            <OrdersTableRowItemAssigned
              key={otherProps['Assigned User'].ID || 'unassigned'}
              namePrefix={namePrefix}
              disabled={submitting || archive}
              change={change}
              required={Boolean(otherProps['Assigned User'].ID)}
            />
          </TableCell>
          {StateID.indexOf(2) !== -1 &&
            <TableCell
              className={classNames(classes.cell, classes.cellFormSm)}
            >
              <OrdersTableRowItemDriver
                key={Driver.ID}
                namePrefix={namePrefix}
                disabled={submitting || archive}
                change={change}
              />
            </TableCell>
          }
          <TableCell
          className={classNames(classes.cell, classes.cellDelivery)}
        >
          <OrdersTableRowItemDelivery
            namePrefix={namePrefix}
            disabled={submitting || archive}
            change={change}
            variant={StateID.indexOf(2) !== -1 ? 'no-driver' : 'default'}
          />
        </TableCell>
          <TableCell className={classes.cell}>
            {Type}
          </TableCell>
          <TableCell
            className={classNames(classes.cell, classes.cellDateOfService)}
          >
            <Link
              href={archive ? `/workflow/archive_order/edit/${otherProps['Order ID']}` : `/workflow/order/edit/${otherProps['Order ID']}`}
              inlineLabel
              breakWord
              target="_blank"
            >
              {`${otherProps['Date of Service From']} - ${otherProps['Date of Service To']}`}
            </Link>
          </TableCell>
          <TableCell className={classes.cell}>
            <Tooltip
              disabled={!expanded}
              content={
                <Typography>
                  {Description}
                </Typography>
              }
              placement="top"
            >
              <Link
                className={classNames(classes.linkTrimmedText, {
                  [classes.linkTrimmedTextVariantCombinedConnected]: ['combined', 'connected', 'workflow'].indexOf(variant) !== -1,
                })}
                onClick={() => {
                  onHCPCCodeClick(Product);
                }}
                inlineLabel
                target="_blank"
              >
                {otherProps['HCPC S Code']['HCPC S Code']}
              </Link>
            </Tooltip>
          </TableCell>
          {intersection([1, 2, 3, 4, 5], StateID).length > 0 &&
            <TableCell
              className={classes.cell}
              {...(['combined', 'connected', 'workflow'].indexOf(variant) !== -1 && intersection([1, 3], StateID).length > 0)
                ? { colSpan: 2 } : {}}
            >
              {(Modifiers && Modifiers.length > 0) && Modifiers.match(/.{1,2}/g).join('-')}
            </TableCell>
          }
          {intersection([1, 2, 3, 4, 5], StateID).length > 0 &&
            <Fragment>
              {Product['Calories Per Unit'] &&
                <TableCell className={classes.cell}>
                  {Math.floor(Units * 100 / Product['Calories Per Unit'])}
                </TableCell>
              }
              <TableCell className={classes.cell}>
                {Units}
              </TableCell>
            </Fragment>
          }
          {intersection([1, 2, 3, 4, 5], StateID).length > 0 &&
            <TableCell
              className={classes.cell}
            >
              {Charge && Units ? formatPrice(Number(Charge) * Units) : ''}
            </TableCell>
          }
          {intersection([4, 5], StateID).length > 0 &&
            <Fragment>
              <TableCell className={classNames(classes.cell, classes.cellBadgeInsurance)}>
                {(Insurance && Insurance.length > 0) &&
                  sortInsurance(Insurance).map(insuranceItem => (
                    <Badge
                      key={insuranceItem.ID}
                      className={classes.badgeInsurance}
                      color="primary"
                      tooltipText={insuranceItem['Insurance Type'] === 'Self'
                        ? 'Self Payer'
                        : insuranceItem['Payer Name']}
                    >
                      {insuranceItem['Order Benefits'] === 'Primary' && '1'}
                      {insuranceItem['Order Benefits'] === 'Secondary' && '2'}
                      {insuranceItem['Order Benefits'] === 'Secondary As Primary' && '2'}
                      {insuranceItem['Order Benefits'] === 'Tertiary' && '3'}
                      {insuranceItem['Insurance Type'] === 'Self' && 'S'}
                      {(!insuranceItem['Order Benefits'] && insuranceItem['Insurance Type'] !== 'Self')
                        && insuranceItem['Payer Name'].charAt(0)}
                    </Badge>
                  ))
                }
              </TableCell>
              <TableCell
                className={classNames(classes.cell, classes.cellReimbs)}
              >
                <OrdersTableRowItemReimbs
                  className={classes.reimbs}
                  namePrefix={namePrefix}
                  disabled={submitting || archive}
                  onBlur={onTextFieldBlur}
                  Insurance={Insurance}
                  orderLineInsuranceParam={otherProps['Order Line Insurance Param']}
                />
              </TableCell>
              <TableCell
                className={classNames(classes.cell, classes.cellReimbsDate)}
                {...['combined', 'connected', 'workflow'].indexOf(variant) !== -1
                  ? { colSpan: 2 } : {}}
              >
                <OrdersTableRowItemReimbsDate
                  className={classes.reimbsDate}
                  namePrefix={namePrefix}
                  disabled={submitting || archive}
                  onBlur={onTextFieldBlur}
                  Insurance={Insurance}
                />
              </TableCell>
              <TableCell className={classNames(classes.cell, classes.cellCheckNumber)}>
                <OrdersTableRowItemCheckNumber
                  className={classes.checkNumber}
                  namePrefix={namePrefix}
                  disabled={submitting || archive}
                  onBlur={onTextFieldBlur}
                  Insurance={Insurance}
                  orderLineID={ID}
                  orderID={otherProps['Order ID']}
                  orderLineInsuranceParam={otherProps['Order Line Insurance Param']}
                />
              </TableCell>
            </Fragment>
          }
          <TableCell
            className={classes.cell}
            numeric
            {...(['connected'].indexOf(variant) !== -1 && intersection([1, 3], StateID).length > 0)
              ? { colSpan: 3 } : {}}
            {...(['workflow', 'combined'].indexOf(variant) !== -1 && intersection([1, 3], StateID).length > 0)
              ? { colSpan: 4 } : {}}
          >
            <Tooltip
              className={classes.tooltipTransmission}
              disabled={!expanded}
              content={
                <Typography>
                  {otherProps['Transmission Method']}
                  {transmissionStatus && `: ${transmissionStatus}`}
                </Typography>
              }
              placement="top"
            >
              {otherProps['Transmission Method'] === 'Electronically'
                && otherProps['Claim Md Claim']
                && otherProps['Claim Md Claim'][0]
                && otherProps['Claim Md Claim'][0].Status ? (
                  <IconButton
                    className={classes.claimLogViewButton}
                    onClick={this.onClaimLogViewButtonClick}
                  >
                    <Typography
                      className={classNames(classes.typographyTransmission, {
                        [classes.typographyTransmissionCreated]: transmissionStatusType === 'Create',
                        [classes.typographyTransmissionApproved]: transmissionStatusType === 'Success',
                        [classes.typographyTransmissionSend]: transmissionStatusType === 'Sent',
                        [classes.typographyTransmissionRejected]: transmissionStatusType === 'Error',
                      })}
                      variant="caption"
                    >
                      {otherProps['Transmission Method'].slice(0, 1)}
                    </Typography>
                  </IconButton>
                ) : (
                  <Typography
                    className={classes.typographyTransmission}
                    variant="caption"
                  >
                    {otherProps['Transmission Method'].slice(0, 1)}
                  </Typography>
                )}
            </Tooltip>
            {(otherProps['Claim Md Claim']
              && otherProps['Claim Md Claim'][0]
              && otherProps['Claim Md Claim'][0].Status) && (
              <ClaimLogViewDialog
                open={claimLogViewDialogOpen}
                onClose={this.onClaimLogViewDialogClose}
                {...{
                  'HCPC S Code': otherProps['HCPC S Code']['HCPC S Code'],
                  'Order ID': otherProps['Order ID'],
                  'Order Line ID': [ID],
                }}
                Claim={otherProps['Claim Md Claim']}
                Insurance={otherProps['Primary Insurance']}
                business={business}
                Patient={Patient}
              />
            )}
          </TableCell>
          {containsNarrativeCMNAMB &&
            <TableCell
              numeric
              className={classes.cell}
              {...(['combined', 'workflow'].indexOf(variant) !== -1 && intersection([1, 2, 3], StateID).length > 0)
                ? { colSpan: 2 } : {}}
            >
              {(Narrative || (otherProps.CMN && otherProps.CMN.ID)) &&
                <Fragment>
                  <Link
                    className={classes.link}
                    color="primary"
                    bold
                    aria-label="More"
                    aria-owns={open ? `${ID}-order-item-more-menu` : null}
                    aria-haspopup="true"
                    onClick={this.onMenuButtonClick}
                  >
                    View
                    <IconView className={classes.iconView} />
                  </Link>
                  <Menu
                    id={`${ID}-order-item-more-menu`}
                    anchorEl={anchorEl}
                    open={open}
                    maxHeight={100}
                    onClose={this.onMenuClose}
                  >
                    {Narrative &&
                      <MenuItem onClick={this.onNarrativeMenuItemClick}>
                        View Narrative
                      </MenuItem>
                    }
                    {(otherProps.CMN && otherProps.CMN.ID) &&
                      <MenuItem onClick={this.onCMNMenuItemClick}>
                        View CMN
                      </MenuItem>
                    }
                  </Menu>
                  {Narrative &&
                    <NarrativeViewDialog
                      open={narrativeViewDialogOpen}
                      onClose={this.onNarrativeViewDialogClose}
                      Narrative={Narrative}
                    />
                  }
                  {(otherProps.CMN && otherProps.CMN.ID) &&
                    <DocumentsViewDialog
                      open={cmnViewDialogOpen}
                      onClose={this.onCMNViewDialogClose}
                      Documents={[otherProps.CMN]}
                      initialDocumentID={otherProps.CMN.ID}
                    />
                  }
                </Fragment>
              }
            </TableCell>
          }
        </TableRow>
      </Fragment>
    );
  }
}

OrdersTableRowItem.propTypes = {
  expanded: PropTypes.bool,
  onTextFieldBlur: PropTypes.func,
  ID: PropTypes.number.isRequired,
  'Order ID': PropTypes.number.isRequired,
  'Workflow Status': PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number.isRequired,
  })),
  Type: PropTypes.string.isRequired,
  'Date of Service From': PropTypes.string.isRequired,
  'Date of Service To': PropTypes.string.isRequired,
  Description: PropTypes.string.isRequired,
  Charge: PropTypes.string.isRequired,
  Units: PropTypes.number.isRequired,
  Reimbs: PropTypes.string,
  Narrative: PropTypes.string,
  Modifiers: PropTypes.string,
  'Assigned User': PropTypes.shape({
    ID: PropTypes.number,
    'First Name': PropTypes.string,
    'Last Name': PropTypes.string,
  }).isRequired,
  Driver: PropTypes.shape({
    ID: PropTypes.number,
    'First Name': PropTypes.string,
    'Last Name': PropTypes.string,
  }),
  Product: PropTypes.shape({
    'Main Hcpc Code': PropTypes.shape({
      ID: PropTypes.number,
    }),
    'Stock Line Balance': PropTypes.number,
  }).isRequired,
  'Transmission Method': PropTypes.string.isRequired,
  Claim: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number,
  })),
  containsNarrativeCMNAMB: PropTypes.bool,
  Insurance: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number.isRequired,
  })),
  'Primary Insurance': PropTypes.shape({
    ID: PropTypes.number,
  }),
  'CMN/AMB': PropTypes.string,
  variant: PropTypes.oneOf([
    'workflow',
    'combined',
    'connected',
  ]).isRequired,
  submitting: PropTypes.bool.isRequired,
  change: PropTypes.func.isRequired,
  editAddressHref: PropTypes.string,
  onStockLineCreate: PropTypes.func,
  onHCPCCodeClick: PropTypes.func,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  width: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']).isRequired,
  business: PropTypes.shape({
    ID: PropTypes.number,
  }),
  Patient: PropTypes.shape({
    'Record Number': PropTypes.number,
  }).isRequired,
  classes: PropTypes.shape({
    row: PropTypes.string.isRequired,
    rowHead: PropTypes.string.isRequired,
    rowBody: PropTypes.string.isRequired,
    cell: PropTypes.string.isRequired,
    cellHidden: PropTypes.string.isRequired,
    cellActions: PropTypes.string.isRequired,
    cellActionsConnectedOrders: PropTypes.string.isRequired,
    cellFormLg: PropTypes.string.isRequired,
    cellFormMd: PropTypes.string.isRequired,
    cellFormSm: PropTypes.string.isRequired,
    selection: PropTypes.string.isRequired,
    iconView: PropTypes.string.isRequired,
    link: PropTypes.string.isRequired,
    linkTrimmedText: PropTypes.string.isRequired,
    linkTrimmedTextVariantCombinedConnected: PropTypes.string.isRequired,
    actions: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    typographyTransmission: PropTypes.string.isRequired,
    typographyTransmissionCreated: PropTypes.string.isRequired,
    typographyTransmissionApproved: PropTypes.string.isRequired,
    typographyTransmissionSend: PropTypes.string.isRequired,
    typographyTransmissionRejected: PropTypes.string.isRequired,
    claimLogViewButton: PropTypes.string.isRequired,
    tooltipAssignedBy: PropTypes.string.isRequired,
    chip: PropTypes.string.isRequired,
    iconArticle: PropTypes.string.isRequired,
  }).isRequired,
};

OrdersTableRowItem.defaultProps = {
  expanded: false,
  Narrative: null,
  Driver: null,
  Modifiers: null,
  Reimbs: null,
  editAddressHref: null,
  containsNarrativeCMNAMB: false,
  onTextFieldBlur: () => {},
  onStockLineCreate: () => {},
  onHCPCCodeClick: () => {},
  business: {},
  Insurance: [],
  'CMN/AMB': null,
  Claim: null,
  'Primary Insurance': null,
};

export default withRouter(
  withWidth()(withStyles(styles)(OrdersTableRowItem)),
);



// WEBPACK FOOTER //
// ./src/components/workflow/OrdersTable/OrdersTableRowItem.js